import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Badge, Box, Divider, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Spinner, Text, Tooltip,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { HiBellAlert } from 'react-icons/hi2';
import { MdCheck } from 'react-icons/md';
import * as NotificationService from 'services/NotificationService';
import { AdminNotification } from 'utils/interfaces';
import { isAdmin } from 'utils/auth-utils';
import { formatDateTime } from 'utils/common-utils';

// How often to poll for new notifications while logged in, so an admin sees a badge/list update
// (e.g. an employee submitting a report) without having to manually refresh the page. No backend
// push (WebSocket/SSE) infra exists in this project, so polling is the pragmatic choice here.
const POLL_INTERVAL_MS = 15000;

// Self-contained: owns its own admin check, data fetching and read-state so it can be dropped into
// any layout with zero wiring at the call site (see NavbarAdmin.tsx) — it renders nothing for
// non-admin users, who share the same navbar.
export default function NotificationBell() {
  const { keycloak } = useKeycloak();
  const history = useHistory();
  const token = keycloak?.token;
  const onSessionExpired = () => keycloak?.logout();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMenuOpenRef = useRef(false);
  isMenuOpenRef.current = isMenuOpen;

  const admin = Boolean(keycloak && isAdmin(keycloak));

  const loadNotifications = async () => {
    if (!token) return;
    setIsLoading(true);
    const data = await NotificationService.getAllPaginated(token, onSessionExpired, 0, 5);
    if (data) setNotifications(data.content);
    setHasLoadedOnce(true);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!admin || !token) return;

    const refreshUnreadCount = () => {
      NotificationService.getUnreadCount(token, onSessionExpired).then(count => {
        if (count !== undefined) setUnreadCount(count);
      });
    };

    refreshUnreadCount();
    const intervalId = setInterval(() => {
      refreshUnreadCount();
      // Menu is lazy-loaded on open, so also refresh its content while it's open — otherwise a new
      // notification would only appear in the list next time it's re-opened.
      if (isMenuOpenRef.current) loadNotifications();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, token]);

  if (!admin) return null;

  const handleMarkAsRead = async (id: number) => {
    if (!token) return;
    const ok = await NotificationService.markAsRead(token, onSessionExpired, id);
    if (ok) {
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleNotificationClick = (notification: AdminNotification) => {
    if (!notification.isRead) handleMarkAsRead(notification.id);
    if (notification.interventionReportId) {
      history.push(`/admin/rapporti-intervento/${notification.interventionReportId}`);
    }
  };

  return (
    <Menu
      isLazy
      onOpen={() => {
        setIsMenuOpen(true);
        loadNotifications();
      }}
      onClose={() => setIsMenuOpen(false)}
    >
      <Box position="relative" display="inline-block">
        <MenuButton
          as={IconButton}
          aria-label="Notifiche"
          icon={<Icon as={HiBellAlert} width="20px" height="20px" />}
          variant="ghost"
          borderRadius="full"
          _focus={{ boxShadow: 'none' }}
        />
        {unreadCount > 0 && (
          <Badge
            position="absolute"
            top="0"
            right="0"
            transform="translate(30%, -30%)"
            borderRadius="full"
            bg="red.500"
            color="white"
            fontSize="10px"
            minW="18px"
            h="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Box>
      <MenuList maxW="380px" p={0} fontFamily="'DM Sans', system-ui, -apple-system, sans-serif">
        <Box px={4} py={3} borderBottomWidth="1px">
          <Text fontWeight="bold" fontSize="sm">Notifiche</Text>
        </Box>

        {isLoading && !hasLoadedOnce ? (
          <Flex justify="center" py={6}><Spinner size="sm" color="gray.500" /></Flex>
        ) : notifications.length === 0 ? (
          <Text px={4} py={6} textAlign="center" fontSize="sm" color="gray.500">Nessuna notifica</Text>
        ) : (
          notifications.map(notification => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              bg={notification.isRead ? undefined : 'blue.50'}
              whiteSpace="normal"
              py={3}
            >
              <Flex justify="space-between" align="start" w="100%" gap={2}>
                <Box flex="1" minW={0}>
                  <Tooltip label={notification.message} placement="top" hasArrow openDelay={300}>
                    <Text fontSize="sm" noOfLines={2}>{notification.message}</Text>
                  </Tooltip>
                  <Text fontSize="xs" color="gray.500" mt={1}>{formatDateTime(notification.createdAt)}</Text>
                </Box>
                {!notification.isRead && (
                  <IconButton
                    aria-label="Segna come letta"
                    icon={<Icon as={MdCheck} />}
                    size="xs"
                    variant="ghost"
                    flexShrink={0}
                    onClick={e => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id);
                    }}
                  />
                )}
              </Flex>
            </MenuItem>
          ))
        )}

        <Divider />
        <MenuItem
          onClick={() => history.push('/admin/notifiche')}
          justifyContent="center"
          fontWeight="600"
          color="#688CC8"
        >
          Visualizza tutte le notifiche
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
